import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @mui components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";

// API service
import autoLicenseService from "services/autoLicenseService";

function AutomationProForm({ license = null, isEdit = false }) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const initialFormData = {
    installname: "",
    installhostname: "",
    edition: "",
    usernumber: "",
    appnumber: "",
    status: "1", // Default to active
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    color: "success",
  });

  // Edition options
  const editionOptions = [
    { value: "1", label: "Basic" },
    { value: "2", label: "Standard" },
    { value: "3", label: "Professional" },
    { value: "4", label: "Enterprise" },
    { value: "5", label: "Trial" }
  ];

  // Status options
  const statusOptions = [
    { value: "1", label: "Active" },
    { value: "0", label: "Inactive" },
    { value: "-1", label: "Suspended" }
  ];

  // If in edit mode, load the license data
  useEffect(() => {
    if (isEdit && license) {
      setFormData({
        installname: license.installname || "",
        installhostname: license.installhostname || "",
        edition: license.edition?.toString() || "",
        usernumber: license.usernumber?.toString() || "",
        appnumber: license.appnumber?.toString() || "",
        status: license.status?.toString() || "1",
      });
    }
  }, [isEdit, license]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error for this field when user makes changes
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.installname.trim()) {
      newErrors.installname = "License name is required";
    }
    
    if (!formData.installhostname.trim()) {
      newErrors.installhostname = "Hostname is required";
    }
    
    if (!formData.edition) {
      newErrors.edition = "Edition is required";
    }
    
    if (!formData.usernumber) {
      newErrors.usernumber = "Number of users is required";
    } else if (isNaN(formData.usernumber) || parseInt(formData.usernumber) < 1) {
      newErrors.usernumber = "Must be a valid number greater than 0";
    }
    
    if (!formData.appnumber) {
      newErrors.appnumber = "Number of workflows is required";
    } else if (isNaN(formData.appnumber) || parseInt(formData.appnumber) < 1) {
      newErrors.appnumber = "Must be a valid number greater than 0";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Convert string values to numbers where appropriate
      const licenseData = {
        ...formData,
        edition: parseInt(formData.edition),
        usernumber: parseInt(formData.usernumber),
        appnumber: parseInt(formData.appnumber),
        status: parseInt(formData.status),
      };
      
      if (isEdit && license) {
        await autoLicenseService.updateLicense(license.id, licenseData);
        setNotification({
          open: true,
          message: "License updated successfully!",
          color: "success",
        });
      } else {
        await autoLicenseService.createLicense(licenseData);
        setNotification({
          open: true,
          message: "License created successfully!",
          color: "success",
        });
        // Reset form after successful creation
        setFormData(initialFormData);
      }
      
      // Navigate back to list after a short delay
      setTimeout(() => {
        navigate("/services/auto");
      }, 1500);
    } catch (error) {
      console.error("Error saving license:", error);
      setNotification({
        open: true,
        message: "Error saving license. Please try again.",
        color: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  return (
    <Card>
      <SoftBox p={3}>
        <SoftTypography variant="h5">
          {isEdit ? "Edit License" : "Add New License"}
        </SoftTypography>
      </SoftBox>
      
      <SoftBox component="form" role="form" p={3} onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.installname}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  License Name<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="installname"
                value={formData.installname}
                onChange={handleChange}
                placeholder="e.g., UDS Automation Pro - Marketing"
                error={!!errors.installname}
              />
              {errors.installname && <FormHelperText>{errors.installname}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.installhostname}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Hostname<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="installhostname"
                value={formData.installhostname}
                onChange={handleChange}
                placeholder="e.g., workflow-server.yourdomain.com"
                error={!!errors.installhostname}
              />
              {errors.installhostname && <FormHelperText>{errors.installhostname}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.edition}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Edition<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                name="edition"
                value={formData.edition}
                onChange={handleChange}
                placeholder="Select Edition"
                error={!!errors.edition}
              >
                {editionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </SoftSelect>
              {errors.edition && <FormHelperText>{errors.edition}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.usernumber}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Number of Users<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="number"
                name="usernumber"
                value={formData.usernumber}
                onChange={handleChange}
                placeholder="e.g., 10"
                error={!!errors.usernumber}
              />
              {errors.usernumber && <FormHelperText>{errors.usernumber}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.appnumber}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Number of Workflows<span style={{ color: "red" }}>*</span>
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="number"
                name="appnumber"
                value={formData.appnumber}
                onChange={handleChange}
                placeholder="e.g., 5"
                error={!!errors.appnumber}
              />
              {errors.appnumber && <FormHelperText>{errors.appnumber}</FormHelperText>}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Status
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </SoftSelect>
            </FormControl>
          </Grid>
        </Grid>
        
        <SoftBox mt={4} mb={1} display="flex" justifyContent="space-between">
          <SoftButton 
            variant="outlined" 
            color="secondary" 
            onClick={() => navigate("/services/auto")}
          >
            Cancel
          </SoftButton>
          <SoftButton
            type="submit"
            variant="gradient"
            color="info"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : (isEdit ? "Update License" : "Add License")}
          </SoftButton>
        </SoftBox>
      </SoftBox>
      
      {/* Notification */}
      <SoftSnackbar
        color={notification.color}
        icon="notifications"
        title="Notification"
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
      />
    </Card>
  );
}

AutomationProForm.propTypes = {
  license: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default AutomationProForm;